import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Database, Server, FileText, Settings, Activity, Cloud, HardDrive, Network, Zap } from "lucide-react";

export default function PlanPage() {
  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-bold text-gray-900">Database Architecture Planning</h1>
        <p className="text-gray-600 mt-1">Strategic planning for database architecture, topology, and Azure SQL deployment strategies</p>
      </div>

      {/* Architecture Planning Tabs */}
      <Tabs defaultValue="topology" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="topology">Logical Topology</TabsTrigger>
          <TabsTrigger value="normalization">Normalization</TabsTrigger>
          <TabsTrigger value="sharding">Sharding Strategy</TabsTrigger>
          <TabsTrigger value="azure">Azure SQL Options</TabsTrigger>
          <TabsTrigger value="hybrid">Hybrid Architecture</TabsTrigger>
        </TabsList>

        {/* Logical Topology Planning */}
        <TabsContent value="topology" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5 text-shiftleft-600" />
                  Database Topology Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium">Single Database</span>
                    <Badge variant="outline">Simple</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium">Multi-Database</span>
                    <Badge variant="outline">Moderate</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium">Distributed</span>
                    <Badge variant="outline">Complex</Badge>
                  </div>
                </div>
                <Button className="w-full">Analyze Current Topology</Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Network className="w-5 h-5 text-green-600" />
                  Schema Relationships
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-sm text-gray-600">
                  <p>Analyze cross-schema dependencies and relationships to determine optimal database boundaries.</p>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Cross-schema references</span>
                    <span className="font-medium">23 found</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Circular dependencies</span>
                    <span className="font-medium text-yellow-600">2 found</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Isolation candidates</span>
                    <span className="font-medium text-green-600">5 schemas</span>
                  </div>
                </div>
                <Button variant="outline" className="w-full">Generate Topology Map</Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Normalization Strategy */}
        <TabsContent value="normalization" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-shiftleft-600" />
                  Normalization Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm font-medium">OLTP Workloads</span>
                    </div>
                    <p className="text-xs text-gray-600">Highly normalized (3NF+)</p>
                  </div>
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                      <span className="text-sm font-medium">Mixed Workloads</span>
                    </div>
                    <p className="text-xs text-gray-600">Selective denormalization</p>
                  </div>
                  <div className="p-3 bg-shiftleft-50 border border-shiftleft-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-2 h-2 bg-shiftleft-500 rounded-full"></div>
                      <span className="text-sm font-medium">OLAP/Reporting</span>
                    </div>
                    <p className="text-xs text-gray-600">Denormalized for performance</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-orange-600" />
                  Workload Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Read/Write Ratio</span>
                    <Badge>70/30</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Query Complexity</span>
                    <Badge variant="outline">High</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Join Operations</span>
                    <Badge variant="outline">Frequent</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Aggregations</span>
                    <Badge variant="outline">Heavy</Badge>
                  </div>
                </div>
                <Button variant="outline" className="w-full">Analyze Workload Patterns</Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-purple-600" />
                  Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium mb-1">Core Tables</p>
                    <p className="text-xs text-gray-600">Keep highly normalized (3NF)</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium mb-1">Reporting Views</p>
                    <p className="text-xs text-gray-600">Create denormalized materialized views</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium mb-1">Lookup Tables</p>
                    <p className="text-xs text-gray-600">Consider selective denormalization</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Sharding Strategy */}
        <TabsContent value="sharding" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="w-5 h-5 text-red-600" />
                  Sharding Assessment
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Data Volume</span>
                      <Badge variant="outline">2.5TB</Badge>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-red-600 h-2 rounded-full" style={{ width: '75%' }}></div>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">Approaching single DB limits</p>
                  </div>
                  
                  <div className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Query Performance</span>
                      <Badge variant="outline">Degrading</Badge>
                    </div>
                    <p className="text-xs text-gray-600">Cross-table joins becoming expensive</p>
                  </div>
                  
                  <div className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Scaling Requirements</span>
                      <Badge>High</Badge>
                    </div>
                    <p className="text-xs text-gray-600">Horizontal scaling needed</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5 text-shiftleft-600" />
                  Sharding Strategies
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="p-3 bg-shiftleft-50 border border-shiftleft-200 rounded-lg">
                    <p className="text-sm font-medium mb-1">Horizontal Partitioning</p>
                    <p className="text-xs text-gray-600">Split by customer, region, or date</p>
                    <Badge className="mt-2" variant="outline">Recommended</Badge>
                  </div>
                  
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-sm font-medium mb-1">Vertical Partitioning</p>
                    <p className="text-xs text-gray-600">Split by feature or domain</p>
                    <Badge className="mt-2" variant="outline">Consider</Badge>
                  </div>
                  
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm font-medium mb-1">Functional Partitioning</p>
                    <p className="text-xs text-gray-600">Separate read/write workloads</p>
                    <Badge className="mt-2" variant="outline">Hybrid</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Azure SQL Options */}
        <TabsContent value="azure" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Cloud className="w-5 h-5 text-shiftleft-600" />
                  SQL on Azure VM
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="text-sm">
                    <p className="font-medium text-green-600 mb-1">✓ Best For:</p>
                    <ul className="text-xs text-gray-600 space-y-1">
                      <li>• Full SQL Server compatibility</li>
                      <li>• Custom configurations</li>
                      <li>• Legacy applications</li>
                      <li>• Always On Availability Groups</li>
                    </ul>
                  </div>
                  <div className="text-sm">
                    <p className="font-medium text-red-600 mb-1">✗ Considerations:</p>
                    <ul className="text-xs text-gray-600 space-y-1">
                      <li>• Full management overhead</li>
                      <li>• OS and SQL patching</li>
                      <li>• Higher operational cost</li>
                    </ul>
                  </div>
                </div>
                <Badge className="w-full justify-center">IaaS</Badge>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5 text-green-600" />
                  Azure SQL Managed Instance
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="text-sm">
                    <p className="font-medium text-green-600 mb-1">✓ Best For:</p>
                    <ul className="text-xs text-gray-600 space-y-1">
                      <li>• Near 100% SQL compatibility</li>
                      <li>• Cross-database queries</li>
                      <li>• SQL Agent jobs</li>
                      <li>• VNet integration</li>
                    </ul>
                  </div>
                  <div className="text-sm">
                    <p className="font-medium text-red-600 mb-1">✗ Considerations:</p>
                    <ul className="text-xs text-gray-600 space-y-1">
                      <li>• Higher cost than SQL DB</li>
                      <li>• Limited scaling options</li>
                      <li>• Minimum 4 vCores</li>
                    </ul>
                  </div>
                </div>
                <Badge className="w-full justify-center" variant="outline">PaaS</Badge>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-purple-600" />
                  Azure SQL Hyperscale
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="text-sm">
                    <p className="font-medium text-green-600 mb-1">✓ Best For:</p>
                    <ul className="text-xs text-gray-600 space-y-1">
                      <li>• Large databases ({'>'}1TB)</li>
                      <li>• Rapid scaling needs</li>
                      <li>• Read scale-out</li>
                      <li>• Fast backup/restore</li>
                    </ul>
                  </div>
                  <div className="text-sm">
                    <p className="font-medium text-red-600 mb-1">✗ Considerations:</p>
                    <ul className="text-xs text-gray-600 space-y-1">
                      <li>• Premium pricing</li>
                      <li>• Limited feature set</li>
                      <li>• No elastic pools</li>
                    </ul>
                  </div>
                </div>
                <Badge className="w-full justify-center" variant="secondary">PaaS+</Badge>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Hybrid Architecture */}
        <TabsContent value="hybrid" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Network className="w-5 h-5 text-indigo-600" />
                  Hybrid Strategy Matrix
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Core OLTP</span>
                      <Badge>SQL Managed Instance</Badge>
                    </div>
                    <p className="text-xs text-gray-600">High compatibility, cross-DB queries</p>
                  </div>
                  
                  <div className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Large Archives</span>
                      <Badge variant="outline">SQL Hyperscale</Badge>
                    </div>
                    <p className="text-xs text-gray-600">Massive scale, fast backup/restore</p>
                  </div>
                  
                  <div className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Always On AG</span>
                      <Badge variant="secondary">SQL on VM</Badge>
                    </div>
                    <p className="text-xs text-gray-600">Full control, custom HA setup</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-gray-600" />
                  Migration Strategy
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="p-3 bg-shiftleft-50 border border-shiftleft-200 rounded-lg">
                    <p className="text-sm font-medium mb-1">Phase 1: Assessment</p>
                    <p className="text-xs text-gray-600">Analyze current workloads and dependencies</p>
                  </div>
                  
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm font-medium mb-1">Phase 2: Pilot Migration</p>
                    <p className="text-xs text-gray-600">Start with non-critical workloads</p>
                  </div>
                  
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm font-medium mb-1">Phase 3: Production</p>
                    <p className="text-xs text-gray-600">Migrate core systems with minimal downtime</p>
                  </div>
                </div>
                <Button className="w-full">Generate Migration Plan</Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}